import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormItem } from 'azure-devops-ui/FormItem';
import { TextField, TextFieldStyle, TextFieldWidth } from 'azure-devops-ui/TextField';
import { ObservableValue } from 'azure-devops-ui/Core/Observable';
import { Card } from 'azure-devops-ui/Card';
import { IProjectContextProvider, IWorkItemManager, writingStyles } from '../../common/types';
import { ValidationResult } from '../../common/services/ogg.service';
import { IHeaderCommandBarItem } from 'azure-devops-ui/HeaderCommandBar';
import { Dropdown, DropdownExpandableButton } from 'azure-devops-ui/Dropdown';
import { DropdownSelection } from 'azure-devops-ui/Utilities/DropdownSelection';
import populateSampleData from './examples';
import { useNavigate, useParams } from 'react-router-dom';
import { useTeachingService } from './Teach';
import $ from 'jquery';
import { Location } from 'azure-devops-ui/Utilities/Position';

export interface IWorkItemFormProps {
  wiManager: IWorkItemManager;
  pcProvider: IProjectContextProvider;
}

const WorkItemForm = forwardRef(({ wiManager, pcProvider }: IWorkItemFormProps, ref) => {
  const wiTitle = new ObservableValue<string>(null);
  const wiDescription = new ObservableValue<string>(null);
  const wiAcceptanceCriteria = new ObservableValue<string>(null);
  const pName = new ObservableValue<string>(null);
  const pDescription = new ObservableValue<string>(null);
  const pTechnologiesUsed = new ObservableValue<string>(null);
  const pLanguage = new ObservableValue<string>(null);
  const workItemCollapsed = new ObservableValue<boolean>(false);
  const projectContextCollapsed = new ObservableValue<boolean>(false);
  const [writingStyleSelection, setWritingStyleSelection] = useState(new DropdownSelection());
  const teachingService = useTeachingService();
  let navigate = useNavigate();
  const { sampleId } = useParams();
  writingStyleSelection.select(0);
  const settingsTeachingPosition = 1;
  const writingStyleTeachingPosition = 2;

  function useSample(option: string) {
    populateSampleData(
      option,
      wiTitle,
      wiDescription,
      wiAcceptanceCriteria,
      pName,
      pDescription,
      pTechnologiesUsed,
      pLanguage,
      pcProvider
    );
    sync();
  }

  useSample(sampleId);

  useImperativeHandle(ref, () => ({
    validate(): ValidationResult {
      if (!wiTitle.value && !wiDescription.value && !wiAcceptanceCriteria.value)
        return {
          isValid: false,
          error: {
            message:
              "Please enter Work Item Details above and press 'Reset'. Alternatively, try 'Use Sample Data' to speed things up.",
          },
        };

      if (!pName.value && !pDescription.value && !pTechnologiesUsed.value)
        return {
          isValid: false,
          error: {
            message:
              "Please enter Project Context above and press 'Reset'. Alternatively, try 'Use Sample Data' to speed things up.",
          },
        };

      return {
        isValid: true,
      };
    },
  }));

  const deleteButtonsWi: IHeaderCommandBarItem[] = [
    {
      iconProps: {
        iconName: 'Delete',
      },
      id: 'useDefaults',
      important: true,
      onActivate: () => {
        wiTitle.value = null;
        wiDescription.value = null;
        wiAcceptanceCriteria.value = null;
        sync();
      },
      tooltipProps: { text: 'Clear Work Item Details form' },
    },
  ];

  const buttonsPc: IHeaderCommandBarItem[] = [
    {
      iconProps: {
        iconName: 'Settings',
      },
      className: 'teach',
      id: 'settings',
      important: true,
      onActivate: () => {
        navigate(`settings`);
        teachingService.markAsDone(settingsTeachingPosition);
      },
      tooltipProps: { text: 'Configure more settings for the Project' },
    },
    {
      iconProps: {
        iconName: 'Delete',
      },
      id: 'useDefaults',
      important: true,
      onActivate: () => {
        pName.value = null;
        pDescription.value = null;
        pTechnologiesUsed.value = null;
        sync();
      },
      tooltipProps: { text: 'Clear Project Context form' },
    },
  ];

  function onChange(
    field: ObservableValue<string>,
    setField: (value: string) => Promise<void>
  ): (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => void {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
      field.value = newValue;
      setField && setField(newValue);
      setProjectContext();
    };
  }

  function getSelectedWritingStyle() {
    if (writingStyleSelection.selectedCount == 0) return writingStyles[0].id;

    const selectedIndex = writingStyleSelection.value[0].beginIndex;
    return writingStyles[selectedIndex].id;
  }

  function setProjectContext() {
    pcProvider.setProjectContext({
      name: pName.value,
      description: pDescription.value,
      technologiesUsed: pTechnologiesUsed.value,
      language: pLanguage.value,
      writingStyle: getSelectedWritingStyle(),
    });
  }

  function sync() {
    wiManager.setTitleField(wiTitle.value);
    wiManager.setDescriptionField(wiDescription.value);
    wiManager.setAcceptanceCriteriaField(wiAcceptanceCriteria.value);
    setProjectContext();
  }

  function onCollapsed(collapsedVariable: ObservableValue<boolean>) {
    collapsedVariable.value = !collapsedVariable.value;
  }

  sync();

  useEffect(() => {
    const teachElement = $('.project-context .teach').get(0);
    const writingStyle = $('.project-context .writing-personality').get(0);
    if (!teachElement || !writingStyle) return;

    teachingService.registerBubble(
      {
        anchorElement: teachElement as HTMLDivElement,
        anchorOrigin: { horizontal: Location.end, vertical: Location.end },
        title: "Configure Ogg's context",
        text: 'In order for Ogg to provide more relevant results, configure the Personas and Definition of Ready',
        onDismiss: () => {},
      },
      settingsTeachingPosition
    );

    teachingService.registerBubble(
      {
        anchorElement: writingStyle as HTMLDivElement,
        anchorOrigin: { horizontal: Location.start, vertical: Location.start },
        title: 'Try some fun writing personalities',
        text: 'Spice it up a bit by giving Ogg different personalities such as Baby Voice, Austin Powers and more',
        onDismiss: () => {},
      },
      writingStyleTeachingPosition
    );

    return () => {
      teachingService.unregisterBubble(settingsTeachingPosition);
      teachingService.unregisterBubble(writingStyleTeachingPosition);
    };
  });

  return (
    <div className='flex-row rhythm-horizontal-16 padding-top-16'>
      <Card
        className='flex-grow work-item-details'
        titleProps={{ text: 'Work Item details', ariaLevel: 3 }}
        headerIconProps={{ iconName: 'WorkItem' }}
        headerCommandBarItems={deleteButtonsWi}
        collapsible={true}
        collapsed={workItemCollapsed}
        onCollapseClick={() => onCollapsed(workItemCollapsed)}
      >
        <div className='flex-column rhythm-vertical-16 full-width' style={{ flexWrap: 'wrap' }}>
          <FormItem label='Title' message='The title of the work item'>
            <TextField
              value={wiTitle}
              onChange={onChange(wiTitle, (x) => wiManager.setTitleField(x))}
              width={TextFieldWidth.auto}
              required
              style={TextFieldStyle.inline}
            />
          </FormItem>
          <FormItem label='Description' message='The description of the work item'>
            <TextField
              value={wiDescription}
              onChange={onChange(wiDescription, (x) => wiManager.setDescriptionField(x))}
              multiline
              rows={7}
              width={TextFieldWidth.auto}
              style={TextFieldStyle.inline}
            />
          </FormItem>
          <FormItem label='Acceptance Criteria' message='The acceptance criteria of the work item'>
            <TextField
              value={wiAcceptanceCriteria}
              onChange={onChange(wiAcceptanceCriteria, (x) =>
                wiManager.setAcceptanceCriteriaField(x)
              )}
              multiline
              rows={10}
              width={TextFieldWidth.auto}
              style={TextFieldStyle.inline}
            />
          </FormItem>
        </div>
      </Card>

      <Card
        className='flex-grow project-context'
        titleProps={{ text: 'Project Context', ariaLevel: 3 }}
        headerIconProps={{ iconName: 'ProjectCollection' }}
        headerCommandBarItems={buttonsPc}
        collapsible={true}
        collapsed={projectContextCollapsed}
        onCollapseClick={() => onCollapsed(projectContextCollapsed)}
      >
        <div className='flex-column rhythm-vertical-16 full-width' style={{ flexWrap: 'wrap' }}>
          <FormItem label='Name' message='The name of the project'>
            <TextField
              value={pName}
              onChange={onChange(pName, null)}
              width={TextFieldWidth.auto}
              style={TextFieldStyle.inline}
            />
          </FormItem>
          <FormItem label='Description' message='The description of the project'>
            <TextField
              value={pDescription}
              onChange={onChange(pDescription, null)}
              multiline
              rows={4}
              width={TextFieldWidth.auto}
              style={TextFieldStyle.inline}
            />
          </FormItem>
          <FormItem label='Technologies Used' message='The technologies used in the project'>
            <TextField
              value={pTechnologiesUsed}
              onChange={onChange(pTechnologiesUsed, null)}
              multiline
              rows={4}
              width={TextFieldWidth.auto}
              style={TextFieldStyle.inline}
            />
          </FormItem>
          <FormItem label='Language' message='The language to use in the Ogg outputs'>
            <TextField
              value={pLanguage}
              onChange={onChange(pLanguage, null)}
              width={TextFieldWidth.auto}
              style={TextFieldStyle.inline}
            />
          </FormItem>
          <FormItem
            label='Writing Personality'
            message='The tone or writing style to be used in the generated output. Warning - things might get fun.'
            className='writing-personality'
          >
            <Dropdown
              placeholder='Select an Option'
              items={writingStyles}
              selection={writingStyleSelection}
              onSelect={() => {
                setProjectContext();
                teachingService.markAsDone(writingStyleTeachingPosition);
              }}
              renderExpandable={(props) => <DropdownExpandableButton {...props} />}
            />
          </FormItem>
        </div>
      </Card>
    </div>
  );
});

export default WorkItemForm;
